import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/ui/PageHeader/PageHeader.tsx';
import useCurrentUser from '@/api/useCurrentUser.ts';
import useAllUserPaperAttempts, { AttemptWithDetails } from '@/api/useAllUserPaperAttempts';
import usePastPapers from '@/api/usePastPapers.ts';
import useAllUserPracticeSets, { PracticeSetWithDetails } from '@/api/useAllUserPracticeSets';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import { ColumnDef } from '@tanstack/react-table';
import Routes from '@/router/Routes.ts';
import { Button } from 'react-daisyui';
import CompletionChart from '@/components/ui/StatCard/CompletionChart';
import AverageGradeChart from '@/components/ui/StatCard/AverageGradeChart';
import useProgressStats from '@/hooks/useProgressStats';
import useCreateAttempt from '@/api/useCreateAttempt';
import useCreatePracticeSet from '@/api/useCreatePracticeSet';
import { useAuthContext } from '@/context/AuthContext';
import MedalBlue from '@/assets/Medal Blue.svg';
import DailyLimitModal from '@/components/ui/DailyLimitModal/DailyLimitModal';
import { primaryColor } from '@/../tailwind.config';

const ProgressTrackerPage: FC = () => {
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const { data: attempts = [] } = useAllUserPaperAttempts(currentUser?.auth_id);
  const { data: pastPapers = [] } = usePastPapers();
  const { data: practiceSets = [], isLoading: isLoadingPracticeSets } = useAllUserPracticeSets(currentUser?.auth_id);
  const [activeTab, setActiveTab] = useState<'papers' | 'questions'>('papers');
  const [retakingAttemptId, setRetakingAttemptId] = useState<string | null>(null);
  const [retakingPracticeSetId, setRetakingPracticeSetId] = useState<string | null>(null);
  const [limitError, setLimitError] = useState<string | null>(null);

  // Retake functionality
  const createAttemptMutation = useCreateAttempt();
  const createPracticeSetMutation = useCreatePracticeSet();
  const { session } = useAuthContext();

  // Calculate statistics using the custom hook
  const stats = useProgressStats(attempts, pastPapers.length);

  // Check if aspirational grade is achieved
  const aspirationalGradeAchieved = useMemo(() => {
    if (!currentUser?.aspirational_grade || !stats.avgGrade) return false;
    const aspirationalGradeNum = parseInt(currentUser.aspirational_grade, 10);
    return !isNaN(aspirationalGradeNum) && stats.avgGrade >= aspirationalGradeNum;
  }, [currentUser?.aspirational_grade, stats.avgGrade]);

  // Data for progress trend chart - show each attempt with full date
  const trendData = useMemo(() => {
    const data = attempts
      .map((attempt, index) => {
        const attemptDate = new Date(attempt.started_at);
        const dataPoint = {
          attemptId: attempt.id,
          paperTitle: attempt.past_papers?.title || 'Unknown Paper',
          grade: attempt.gradeOutOf9 || 0, // Use grade out of 9
          rawMarks: `${attempt.final_grade || 0}/${attempt.totalPossibleMarks || 0}`, // Show raw marks in tooltip
          // Use date + time for uniqueness, but display only date on X-axis
          date: attemptDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          }),
          dateWithTime: `${attemptDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          })} ${attemptDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
          })}`,
          fullDate: attemptDate.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
          timestamp: attemptDate.getTime(),
          // Add unique key for Recharts
          uniqueKey: `${attempt.id}-${index}`,
        };
        return dataPoint;
      })
      .sort((a, b) => a.timestamp - b.timestamp); // Sort by date ascending

    return data;
  }, [attempts]);

  // Calculate time spent (in seconds to readable format)
  const formatTimeSpent = (startedAt: string, submittedAt: string | null) => {
    if (!submittedAt) return 'N/A';
    const start = new Date(startedAt).getTime();
    const end = new Date(submittedAt).getTime();
    const seconds = Math.floor((end - start) / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toString().padStart(2, '0')}s`;
  };

  // Format date with time
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatMonthYearDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  };

  // DataTable columns
  const columns: ColumnDef<AttemptWithDetails>[] = [
    {
      accessorKey: 'past_papers.title',
      header: "Paper's name",
      cell: (info) => (
        <div>
          <p className="text-sm">{info.getValue() as string}</p>
        </div>
      ),
    },
    {
      id: 'subject',
      header: 'Subject',
      cell: (info) => {
        const attempt = info.row.original as AttemptWithDetails;
        return <p className="text-sm">{attempt.past_papers?.subject || '—'}</p>;
      },
    },
    {
      id: 'paper_number',
      header: 'Paper',
      cell: (info) => {
        const attempt = info.row.original as AttemptWithDetails;
        return <p className="text-sm">{attempt.past_papers?.paper_number || '—'}</p>;
      },
    },
    {
      accessorKey: 'past_papers.date',
      header: 'Past Paper Date',
      cell: (info) => <p className="text-sm">{formatMonthYearDateTime(info.getValue() as string)}</p>,
    },
    {
      accessorKey: 'started_at',
      header: 'Date',
      cell: (info) => <p className="text-sm">{formatDateTime(info.getValue() as string)}</p>,
    },
    {
      accessorKey: 'submitted_at',
      header: 'Time spent',
      enableSorting: false,
      cell: (info) => {
        const attempt = info.row.original;
        return <p className="text-sm">{formatTimeSpent(attempt.started_at, attempt.submitted_at)}</p>;
      },
    },
    {
      accessorKey: 'final_grade',
      header: 'Grade',
      cell: (info) => {
        const attempt = info.row.original;
        return (
          <p className="text-sm font-medium text-primary font-bold">
            {attempt.gradeOutOf9} ({attempt.final_grade}/{attempt.totalPossibleMarks})
          </p>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <button
          className="text-sm text-gray-700 hover:text-primary transition-colors font-bold"
          onClick={() => navigate(`/paper-attempts/${info.row.original.id}/results`)}
        >
          Show feedback
        </button>
      ),
    },
    {
      id: 'retake',
      header: 'Actions',
      cell: (info) => {
        const attemptId = info.row.original.id;
        const isLoading = retakingAttemptId === attemptId;
        const isAnyLoading = retakingAttemptId !== null;

        return (
          <button
            className="text-sm text-primary transition-colors font-bold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={async () => {
              if (!session?.user?.id || !info.row.original.past_paper_id) return;
              setRetakingAttemptId(attemptId);
              try {
                const createdAttempt = await createAttemptMutation.mutateAsync({
                  authId: session.user.id,
                  pastPaperId: info.row.original.past_paper_id,
                });

                // Navigate to take paper page
                navigate(`/paper-attempts/${createdAttempt.id}`);
              } catch (error) {
                console.error('Failed to create attempt:', error);
                const errorMessage =
                  error instanceof Error ? error.message : 'Failed to create past paper attempt. Please try again.';

                setLimitError(errorMessage);
              } finally {
                setRetakingAttemptId(null);
              }
            }}
            disabled={isAnyLoading}
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner loading-xs"></span>
                Loading...
              </>
            ) : (
              'Retake Paper'
            )}
          </button>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Progress Tracker"
        showBackButton
        onBackClick={() => navigate(Routes.DASHBOARD)}
        titleAlign="left"
      >
        <p className="text-sm text-gray-600 text-left -mt-4">
          Completed papers, current grade, historic papers, grades and feedback
        </p>
      </PageHeader>
      <div className="flex flex-col gap-3">
        {/* Tabs */}
        <div className="flex gap-4 border-b border-gray-200 justify-center">
          <button
            className={`px-4 py-2 text-lg font-medium transition-colors ${
              activeTab === 'papers'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('papers')}
          >
            Past Papers
          </button>
          <button
            className={`px-4 py-2 text-lg font-medium transition-colors ${
              activeTab === 'questions'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('questions')}
          >
            Practice Sets
          </button>
        </div>

        {activeTab === 'papers' && (
          <>
            {attempts.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center max-w-md">
                  <p className="text-lg text-gray-700 mb-6">
                    You haven't completed any past papers yet. Complete your first paper to see your progress here!
                  </p>
                  <Button
                    size="lg"
                    className="bg-[#2B3440] hover:bg-dark-hover-secondary border-none text-white px-8"
                    onClick={() => navigate(Routes.FULL_PAST_PAPERS)}
                  >
                    Past Papers
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Stats Cards */}
                <div className="flex flex-wrap gap-3 justify-center">
                  <CompletionChart completedCount={stats.completedPapers} totalCount={stats.totalPapers} size="large" />
                  <div className="relative">
                    <AverageGradeChart
                      avgGrade={stats.avgGrade}
                      gradeImprovement={stats.gradeImprovement}
                      size="large"
                    />
                    {aspirationalGradeAchieved && (
                      <img src={MedalBlue} alt="Exam Ready Badge" className="w-20 h-auto absolute top-px" />
                    )}
                  </div>
                </div>

                {/* DataTable */}
                <div className="bg-white rounded-[20px] border border-[#CCCCCC] border-opacity-50 overflow-hidden">
                  <DataTable data={attempts} columns={columns} wrapperClassName="rounded-[20px]" showPagination />
                </div>

                {/* Progress Trend Chart */}
                {trendData.length > 1 && (
                  <div className="bg-white rounded-[20px] border border-[#CCCCCC] border-opacity-50 p-6">
                    <h3 className="text-lg font-semibold mb-4">Grade Progress Over Time</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={trendData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="dateWithTime" tick={{ fontSize: 12 }} stroke="#999" />
                        <YAxis
                          domain={[0, 9]}
                          ticks={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9]}
                          tick={{ fontSize: 12 }}
                          stroke="#999"
                        />
                        <Tooltip
                          content={(props) => {
                            const { active, payload } = props;
                            if (!active || !payload || payload.length === 0) {
                              return null;
                            }

                            const dataPoint = payload[0].payload;

                            return (
                              <div className="bg-white border border-gray-300 rounded-lg p-3 shadow-lg">
                                <div className="font-semibold text-sm mb-1">{dataPoint.paperTitle}</div>
                                <div className="text-xs text-gray-600 mb-2">{dataPoint.fullDate}</div>
                                <div className="text-sm">
                                  <span className="font-medium text-primary">Grade: {dataPoint.grade}</span>
                                  <span className="text-gray-600 ml-2">({dataPoint.rawMarks})</span>
                                </div>
                              </div>
                            );
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="grade"
                          stroke={primaryColor}
                          strokeWidth={2}
                          dot={{ fill: primaryColor, r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {activeTab === 'questions' && (
          <>
            {isLoadingPracticeSets ? (
              <div className="flex items-center justify-center py-20">
                <p className="text-gray-500">Loading practice sets...</p>
              </div>
            ) : practiceSets.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center max-w-md">
                  <p className="text-lg text-gray-700 mb-6">
                    You haven't completed any practice sets yet. Start practicing to see your progress here!
                  </p>
                  <Button
                    size="lg"
                    className="bg-[#2B3440] hover:bg-dark-hover-secondary border-none text-white px-8"
                    onClick={() => navigate(Routes.PRACTICE_AREA)}
                  >
                    Practice Questions
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-[20px] border border-[#CCCCCC] border-opacity-50 overflow-hidden">
                <DataTable
                  data={practiceSets}
                  columns={[
                    {
                      accessorKey: 'question_category',
                      header: 'Category',
                      cell: (info) => {
                        const practiceSet = info.row.original as PracticeSetWithDetails;
                        const questionCategory = practiceSet.question_category;

                        // Mixed practice set
                        if (questionCategory === 'mixed' || questionCategory === 'Mixed') {
                          return (
                            <div className="flex flex-col gap-0.5">
                              <p className="text-sm font-medium">Mixed Practice</p>
                              <p className="text-xs text-gray-500">Multiple categories</p>
                            </div>
                          );
                        }

                        // Mocked past paper set
                        if (questionCategory === 'MockedPastPaper') {
                          return (
                            <div className="flex flex-col gap-0.5">
                              <p className="text-sm font-medium">Mock Paper</p>
                              <p className="text-xs text-gray-500">Full paper simulation</p>
                            </div>
                          );
                        }

                        // Regular practice set — derive subject/paper from category_code
                        const firstQuestion = practiceSet.practice_answers?.[0]?.questions;
                        const categoryCode = firstQuestion?.category_code;

                        let subject: string | null = null;
                        let paper: string | null = null;
                        if (categoryCode) {
                          const parts = categoryCode.split('-');
                          if (parts[0] === 'LANG') subject = 'English Language';
                          else if (parts[0] === 'LIT') subject = 'English Literature';
                          if (parts[1] === 'P1') paper = 'Paper 1';
                          else if (parts[1] === 'P2') paper = 'Paper 2';
                        }

                        return (
                          <div className="flex flex-col gap-0.5">
                            <p className="text-sm font-medium">{categoryCode || questionCategory}</p>
                            {subject && paper && (
                              <p className="text-xs text-gray-500">{subject} · {paper}</p>
                            )}
                          </div>
                        );
                      },
                    },
                    {
                      accessorKey: 'set_size',
                      header: 'Questions',
                      cell: (info) => <p className="text-sm">{info.getValue() as number}</p>,
                    },
                    {
                      accessorKey: 'created_at',
                      header: 'Date',
                      cell: (info) => {
                        const date = new Date(info.getValue() as string);
                        return (
                          <p className="text-sm">
                            {date.toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        );
                      },
                    },
                    {
                      id: 'score',
                      header: 'Score',
                      cell: (info) => {
                        const practiceSet = info.row.original as PracticeSetWithDetails;

                        // For MockedPastPaper, only count the answered Section B question
                        const isMockedPastPaper = practiceSet.question_category === 'MockedPastPaper';

                        let totalScore = 0;
                        let maxScore = 0;

                        if (isMockedPastPaper) {
                          // Filter out Section B answers first
                          const regularAnswers = practiceSet.practice_answers.filter(
                            (a) => a.questions?.category !== 'SectionB'
                          );
                          const sectionBAnswers = practiceSet.practice_answers.filter(
                            (a) => a.questions?.category === 'SectionB'
                          );

                          // Add regular answers
                          regularAnswers.forEach((answer) => {
                            totalScore += answer.ai_score || 0;
                            maxScore += answer.questions?.marks || 0;
                          });

                          // Add the graded Section B answer (answered or unanswered)
                          const gradedSectionB =
                            sectionBAnswers.find((a) => a.graded_at) || sectionBAnswers[0]; // Fallback to first if none graded yet

                          if (gradedSectionB) {
                            totalScore += gradedSectionB.ai_score || 0;
                            maxScore += gradedSectionB.questions?.marks || 0;
                          }
                        } else {
                          // For regular practice sets, count all answers
                          totalScore = practiceSet.practice_answers.reduce((sum, answer) => {
                            return sum + (answer.ai_score || 0);
                          }, 0);
                          maxScore = practiceSet.practice_answers.reduce((sum, answer) => {
                            return sum + (answer.questions?.marks || 0);
                          }, 0);
                        }

                        const percentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;

                        // Calculate grade out of 9 for MockedPastPaper (same as past papers)
                        let gradeOutOf9 = 0;
                        if (isMockedPastPaper && maxScore > 0) {
                          const percentageDecimal = totalScore / maxScore;
                          if (percentageDecimal >= 0.9) gradeOutOf9 = 9;
                          else if (percentageDecimal >= 0.8) gradeOutOf9 = 8;
                          else if (percentageDecimal >= 0.7) gradeOutOf9 = 7;
                          else if (percentageDecimal >= 0.6) gradeOutOf9 = 6;
                          else if (percentageDecimal >= 0.5) gradeOutOf9 = 5;
                          else if (percentageDecimal >= 0.4) gradeOutOf9 = 4;
                          else if (percentageDecimal >= 0.3) gradeOutOf9 = 3;
                          else if (percentageDecimal >= 0.2) gradeOutOf9 = 2;
                          else if (percentageDecimal >= 0.1) gradeOutOf9 = 1;
                        }

                        return (
                          <p className="text-sm font-medium text-primary font-bold">
                            {isMockedPastPaper && gradeOutOf9 > 0 ? (
                              <>
                                Grade {gradeOutOf9} {totalScore}/{maxScore} ({percentage}%)
                              </>
                            ) : (
                              <>
                                {totalScore}/{maxScore} ({percentage}%)
                              </>
                            )}
                          </p>
                        );
                      },
                    },
                    {
                      id: 'actions',
                      header: 'Actions',
                      cell: (info) => {
                        const practiceSet = info.row.original as PracticeSetWithDetails;
                        return (
                          <button
                            className="text-sm text-gray-700 hover:text-primary transition-colors font-bold"
                            onClick={() => navigate(`/practice-sets/${practiceSet.id}/results`)}
                          >
                            Show feedback
                          </button>
                        );
                      },
                    },
                    {
                      id: 'retake-quiz',
                      header: 'Actions',
                      cell: (info) => {
                        const practiceSet = info.row.original as PracticeSetWithDetails;
                        const practiceSetId = practiceSet.id;
                        const isLoading = retakingPracticeSetId === practiceSetId;
                        const isAnyLoading = retakingPracticeSetId !== null;

                        return (
                          <button
                            className="text-sm text-primary transition-colors font-bold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={async () => {
                              if (!session?.user?.id) return;
                              setRetakingPracticeSetId(practiceSetId);
                              try {
                                const newPracticeSet = await createPracticeSetMutation.mutateAsync({
                                  authId: session.user.id,
                                  retryPracticeSetId: practiceSetId,
                                });

                                // Navigate to the new practice set
                                navigate(`/practice-sets/${newPracticeSet.practice_set_id}`);
                              } catch (error) {
                                console.error('Failed to create practice set:', error);
                                // Extract error message
                                const errorMessage =
                                  error instanceof Error
                                    ? error.message
                                    : 'Failed to create practice set. Please try again.';

                                setLimitError(errorMessage);
                              } finally {
                                setRetakingPracticeSetId(null);
                              }
                            }}
                            disabled={isAnyLoading}
                          >
                            {isLoading ? (
                              <>
                                <span className="loading loading-spinner loading-xs"></span>
                                Loading...
                              </>
                            ) : (
                              'Retake Set'
                            )}
                          </button>
                        );
                      },
                    },
                  ]}
                  wrapperClassName="rounded-[20px]"
                  showPagination
                />
              </div>
            )}
          </>
        )}
        {limitError && <DailyLimitModal errorMessage={limitError} onClose={() => setLimitError(null)} />}
      </div>
    </>
  );
};

export default ProgressTrackerPage;
